/**
 * AiService.js
 * Centralized service for InstilAI performance analysis.
 * Fixed for production builds while maintaining all tracking and sanitization features.
 * UPGRADES: Added robust retry logic, timeouts, and enhanced JSON sanitization for production reliability.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase'; 

/**
 * Service to handle communication with the InstilAI Backend for 
 * generating personalized student analysis.
 * Note: Added a 30-second timeout to prevent the client from hanging on cold starts.
 */
const generateInsights = httpsCallable(functions, 'getTutorInsights', { timeout: 30000 });

/**
 * Helper function to delay execution (used for exponential backoff)
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetches personalized tutor insights based on a student's mistake patterns.
 * * @param {string} prompt - The structured analysis context generated by the AnalyticsEngine.
 * @param {number} maxRetries - Maximum number of retry attempts for network/rate-limit errors (default: 2).
 * @returns {Promise<Object|null>} - A JSON object mapping syllabus topics to AI insights, or null on failure.
 */
export const fetchAiTutorInsights = async (prompt, maxRetries = 2) => {
    // Prevent unnecessary network calls if the prompt is missing
    if (!prompt) {
        console.error("AI Insight Fetch Failed: No prompt provided to the service.");
        return null;
    }

    // --- Performance Tracking (Preserved) ---
    const startTime = Date.now();
    let attempt = 0;

    while (attempt <= maxRetries) {
        try {
            console.log(`Initiating request to InstilAI for performance insights... (Attempt ${attempt + 1}/${maxRetries + 1})`);
            
            // Call the cloud function with the structured prompt
            const result = await generateInsights({ prompt });
            
            /**
             * The backend returns a JSON object where:
             * Key = Syllabus Topic Name
             * Value = 3-sentence analytical insight
             */
            if (result.data && result.data.insights) {
                const duration = ((Date.now() - startTime) / 1000).toFixed(2);
                console.log(`Tutor Insights retrieved successfully in ${duration}s.`);
                
                // --- Sanitization Logic (Enhanced) ---
                let finalizedInsights = result.data.insights;
                
                if (typeof finalizedInsights === 'string') {
                    try {
                        // Clean potential markdown artifacts and fix trailing commas (common LLM hallucination)
                        const cleanedString = finalizedInsights
                            .replace(/```json|```/gi, "")
                            .replace(/,\s*([\]}])/g, "$1") // Removes trailing commas before closing braces/brackets
                            .trim();
                            
                        finalizedInsights = JSON.parse(cleanedString);
                    } catch (parseErr) {
                        console.error("AI returned string but parsing failed:", parseErr, "\nRaw payload:", finalizedInsights);
                        return null; // Fail gracefully so the report still generates with Admin rules
                    }
                }

                return finalizedInsights;
            }

            console.warn("AI service returned an unexpected data structure or empty insights.");
            return null;

        } catch (error) {
            attempt++;
            
            // Determine if the error is worth retrying (e.g., rate limits, timeouts, unavailable)
            const isRetryableError = 
                error.code === 'resource-exhausted' || 
                error.code === 'unavailable' || 
                error.code === 'deadline-exceeded';
            
            if (attempt > maxRetries || !isRetryableError) {
                /**
                 * Graceful degradation (Preserved): If the AI backend fails permanently, 
                 * we log the error and return null so the ReportGenerator proceeds.
                 */
                console.error("AI Insight Fetch Failed permanently:", {
                    code: error.code,
                    message: error.message,
                    details: error.details
                });
                return null; 
            }

            // Exponential backoff calculation: 2s, 4s, 8s...
            const backoffTime = Math.pow(2, attempt) * 1000; 
            console.warn(`AI Fetch network delay/limit reached. Retrying in ${backoffTime}ms...`);
            await delay(backoffTime);
        }
    }
    
    return null; // Fallback return
};